import type { ReactNode } from 'react';

/**
 * The app's KPI tile: uppercase label, tinted icon chip, one big number.
 *
 * Extracted from `financial/sections/MetricCardsRow.tsx`, which had the only
 * copy of this idiom. It is now shared so new surfaces read as part of the same
 * app rather than as a bolted-on panel — the visual signature of this tile is
 * most of what makes a dashboard here look native.
 */
export interface StatTileProps {
  /** Short, uppercase in render. Keep it to two or three words. */
  label: string;
  /** The figure. Pass a node when it needs mixed weight or a unit suffix. */
  value: ReactNode;
  /** One line of context under the value. */
  sub?: ReactNode;
  icon?: ReactNode;
  /** Tailwind background class for the icon chip, e.g. "bg-emerald-50". */
  iconBg?: string;
  /** Tailwind text colour for the icon itself, e.g. "text-emerald-600". */
  iconColor?: string;
  /** Applied to the value line — for a monospace score, or a state colour. */
  valueClassName?: string;
  className?: string;
}

export default function StatTile({
  label,
  value,
  sub,
  icon,
  iconBg = 'bg-neutral-100',
  iconColor = 'text-neutral-500',
  valueClassName = '',
  className = '',
}: StatTileProps) {
  return (
    <div
      className={`rounded-xl border border-neutral-200 bg-white p-4 shadow-sm ${className}`}
    >
      <div className="mb-3 flex items-start justify-between">
        <p className="text-xs font-medium uppercase leading-tight tracking-wide text-neutral-500">
          {label}
        </p>
        {icon && (
          <div
            className={`ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconBg}`}
          >
            <span className={iconColor}>{icon}</span>
          </div>
        )}
      </div>
      <p className={`text-xl font-bold leading-tight text-neutral-900 ${valueClassName}`}>
        {value}
      </p>
      {sub && <div className="mt-1 text-xs text-neutral-400">{sub}</div>}
    </div>
  );
}
